import React from "react";
import { useNavigate } from "react-router-dom";
import { useDashboardData } from "../hooks/useDashboardData";
import {
  DashboardLoading,
  DashboardEmpty,
  NeedsReviewBanner,
  KpiGrid,
  MonthlySpendChart,
  TopPlates,
  AuthorityChart,
  UpcomingDue,
  AnomaliesCard,
  CreditCollectionsCard,
  ViewDocumentsCta,
} from "../components/dashboard/DashboardSections";

export default function Dashboard() {
  const { data, loading } = useDashboardData();
  const navigate = useNavigate();

  if (loading) {
    return <DashboardLoading />;
  }

  const empty = !data || data.total_documents === 0;

  if (empty) {
    return (
      <div className="space-y-6">
        <PageHeader title="Overview" subtitle="Summary of your extracted toll bills" />
        <DashboardEmpty onUpload={() => navigate("/upload")} />
      </div>
    );
  }

  const authChart = (data.by_authority || []).map((a) => ({
    name: a.authority.length > 22 ? a.authority.slice(0, 21) + "\u2026" : a.authority,
    full: a.authority,
    amount: Number(a.amount.toFixed(2)),
  }));

  const monthChart = (data.monthly_spend || []).map((m) => ({
    month: m.month,
    amount: Number((m.amount || 0).toFixed(2)),
  }));
  const topPlates = data.by_plate || [];
  const anomalies = data.anomalies || [];
  const upcoming = data.upcoming_due || [];
  const needsReview = data.needs_review_count || 0;
  const creditCollections = data.credit_collections || { count: 0, amount: 0, authorities: [] };

  return (
    <div className="space-y-6">
      <PageHeader title="Overview" subtitle="Summary of your extracted toll bills" />

      {needsReview > 0 && (
        <NeedsReviewBanner count={needsReview} onReview={() => navigate("/review")} />
      )}

      <KpiGrid data={data} needsReview={needsReview} anomalies={anomalies} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <MonthlySpendChart monthChart={monthChart} />
        <TopPlates topPlates={topPlates} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AuthorityChart authChart={authChart} />
        <UpcomingDue upcoming={upcoming} />
      </div>

      {anomalies.length > 0 && (
        <AnomaliesCard anomalies={anomalies} onOpen={(docId) => navigate(`/documents/${docId}`)} />
      )}

      {creditCollections.count > 0 && (
        <CreditCollectionsCard creditCollections={creditCollections} onOpen={(docId) => navigate(`/documents/${docId}`)} />
      )}

      <ViewDocumentsCta onView={() => navigate("/documents")} />
    </div>
  );
}

export const PageHeader = ({ title, subtitle, right }) => (
  <div className="flex items-end justify-between gap-4">
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    {right}
  </div>
);
